<template>
  <label
    :style="labelStyle"
    :class="labelKls"
    @click.capture="onLabelCapture"
    @click="onClickRoot"
  >
    <input
      v-model="model"
      :class="ns.be('card', 'original')"
      type="checkbox"
      :name="name"
      :tabindex="tabindex"
      :disabled="isDisabled"
      :value="label"
      @change="handleChange"
      @focus="isFocused = true"
      @blur="isFocused = false"
      @click.stop
    />
    <span
      v-if="isCornerCard && isChecked"
      :class="ns.be('card', 'corner')"
      aria-hidden="true"
    >
      <span />
    </span>
    <div :class="ns.be('card', 'content')">
      <span v-if="!isCornerCard" :class="cardInnerStyle" />
      <jd-icon v-if="icon || $slots.icon" :size="iconSize" :color="iconColor">
        <component :is="icon" v-if="icon" />
        <slot v-else name="icon" />
      </jd-icon>
      <div ref="contentTextRef" :class="ns.be('card', 'content_text')">
        <span
          v-if="$slots.default || label"
          :class="[
            ns.be('card', 'title'),
            ns.is('card__title-checked', isChecked),
          ]"
          :style="isChecked ? activeStyle : undefined"
        >
          <slot>{{ label }}</slot>
        </span>
        <span v-if="slots.desc" :class="ns.be('card', 'desc')">
          <slot name="desc" />
        </span>
        <span v-else-if="desc" :class="ns.be('card', 'desc')">
          {{ desc }}
        </span>
      </div>
    </div>
  </label>
</template>

<script lang="ts" setup>
import { computed, inject, provide, ref, useSlots } from 'vue'
import { iconPropType } from '@jdesign-vue/utils'
import { useNamespace } from '@jdesign-vue/hooks'
import { checkboxGroupContextKey } from './constants'
import { useCheckbox } from './composables'
import { checkboxEmits, checkboxProps } from './checkbox'

import type { CSSProperties, PropType } from 'vue'
import type { CheckboxCardVariant } from './checkbox'

defineOptions({
  name: 'JdCheckboxCard',
})

const props = defineProps({
  ...checkboxProps,
  desc: {
    type: String,
  },
  icon: {
    type: iconPropType,
  },
  iconColor: {
    type: String,
  },
  iconSize: {
    type: Number,
    default: 44,
  },
  checkedPostion: {
    type: String,
    default: 'right',
  },
  /**
   * 仅卡片生效。不传则跟随 Group 的 `cardVariant`，再回落到 `classic`。
   */
  cardVariant: {
    type: String as PropType<CheckboxCardVariant>,
    default: undefined,
  },
})
defineEmits(checkboxEmits)
const slots = useSlots()

const { isFocused, isChecked, isDisabled, model, handleChange, onClickRoot } =
  useCheckbox(props, slots)
const checkboxGroup = inject(checkboxGroupContextKey, undefined)
const ns = useNamespace('checkbox')

const contentTextRef = ref<HTMLElement>()

//去掉插槽内注入依赖
provide(checkboxGroupContextKey, undefined)

//去掉label的关联引用
const INTERACTIVE_SELECTOR = computed(() => {
  const nsPrefix = ns.namespace.value
  return `.${nsPrefix}-select, .${nsPrefix}-select-v2, .${nsPrefix}-cascader`
})
const onLabelCapture = (e: MouseEvent) => {
  if (contentTextRef.value?.contains(e.target as Node)) {
    if ((e.target as HTMLElement).closest(INTERACTIVE_SELECTOR.value)) {
      e.preventDefault()
    }
  }
}

const activeStyle = computed<CSSProperties>(() => {
  const fillValue = checkboxGroup?.fill?.value ?? ''
  return {
    backgroundColor: fillValue,
    borderColor: fillValue,
    color: checkboxGroup?.textColor?.value ?? '',
    boxShadow: fillValue ? `-1px 0 0 0 ${fillValue}` : undefined,
  }
})

const mergedCardVariant = computed<CheckboxCardVariant>(
  () =>
    props.cardVariant ?? checkboxGroup?.cardVariant?.value ?? 'classic'
)
const isCornerCard = computed(() => mergedCardVariant.value === 'corner')

const labelKls = computed(() => {
  return [
    ns.b('card'),
    isCornerCard.value ? ns.bm('card', 'corner') : '',
    ns.is('disabled', isDisabled.value),
    ns.is('checked', isChecked.value),
    ns.is('focus', isFocused.value),
  ]
})
const labelStyle = computed(() => {
  const icon = slots.icon?.()
  const width = props.icon || icon ? Number(props.iconSize) + 144 : 144
  return {
    'max-width': `${width + (props.checkedPostion === 'left' ? 24 : 0)}px`,
  }
})
const cardInnerStyle = computed(() => {
  return [
    ns.be('card', 'inner'),
    props.checkedPostion === 'left'
      ? ns.be('card', 'inner-left')
      : ns.be('card', 'inner-right'),
  ]
})
</script>
